import { z } from 'zod';
import { MAX_NAME_LENGTH, MIN_NAME_LENGTH } from './constants';

const playerId = z.string().min(1).max(64);

/**
 * Raw name as typed. Normalisation (whitespace collapse) and uniqueness are
 * applied by the domain layer, not here, so that " Ana " is accepted and
 * stored as "Ana" rather than rejected.
 */
const nameInput = z
  .string()
  .min(MIN_NAME_LENGTH)
  .max(MAX_NAME_LENGTH * 4)
  .refine((v) => v.trim().length >= MIN_NAME_LENGTH, { message: 'name is blank' });

export const JoinIntentSchema = z.object({
  type: z.literal('join'),
  name: nameInput,
});

export const VoteIntentSchema = z.object({
  type: z.literal('vote'),
  /** Carried so a vote for a stale session can be discarded. */
  sessionId: z.string().min(1),
  choice: z.enum(['JA', 'NEIN']),
});

export const SetOwnNameIntentSchema = z.object({
  type: z.literal('setOwnName'),
  name: nameInput,
});

export const StartSessionIntentSchema = z.object({
  type: z.literal('startSession'),
  presidentId: playerId,
  chancellorId: playerId,
});

export const AnnulSessionIntentSchema = z.object({ type: z.literal('annulSession') });

export const SetGovernmentIntentSchema = z.object({
  type: z.literal('setGovernment'),
  presidentId: playerId,
  chancellorId: playerId,
});

export const ExecutePlayerIntentSchema = z.object({
  type: z.literal('executePlayer'),
  playerId,
});

export const RevivePlayerIntentSchema = z.object({
  type: z.literal('revivePlayer'),
  playerId,
});

export const KickPlayerIntentSchema = z.object({
  type: z.literal('kickPlayer'),
  playerId,
});

export const RenamePlayerIntentSchema = z.object({
  type: z.literal('renamePlayer'),
  playerId,
  name: nameInput,
});

export const SetProjectionIntentSchema = z.object({
  type: z.literal('setProjection'),
  mode: z.enum(['LIVE', 'HISTORY']),
});

export const ResetRoomIntentSchema = z.object({ type: z.literal('resetRoom') });

export const CloseRoomIntentSchema = z.object({ type: z.literal('closeRoom') });

export const IntentSchema = z.discriminatedUnion('type', [
  JoinIntentSchema,
  VoteIntentSchema,
  SetOwnNameIntentSchema,
  StartSessionIntentSchema,
  AnnulSessionIntentSchema,
  SetGovernmentIntentSchema,
  ExecutePlayerIntentSchema,
  RevivePlayerIntentSchema,
  KickPlayerIntentSchema,
  RenamePlayerIntentSchema,
  SetProjectionIntentSchema,
  ResetRoomIntentSchema,
  CloseRoomIntentSchema,
]);

export type Intent = z.infer<typeof IntentSchema>;
export type IntentType = Intent['type'];

/** Everything except these two requires the admin token. */
export const PLAYER_INTENTS: readonly IntentType[] = ['join', 'vote', 'setOwnName'];

export function isAdminIntent(type: IntentType): boolean {
  return !PLAYER_INTENTS.includes(type);
}

/**
 * Creating a room only records whether the admin plays. If they do, they then
 * join through the normal `join` flow (same name + avatar experience as guests),
 * and their roster entry is flagged as admin because they hold the admin token.
 */
export const CreateRoomBodySchema = z.object({
  adminIsPlaying: z.boolean(),
});
export type CreateRoomBody = z.infer<typeof CreateRoomBodySchema>;
